import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import ProtectedAction from '../ProtectedAction';
import {
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import {
  faArrowRotateRight,
  faFileExport
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonIcon, useToast } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IGetSubmissionWorkflowResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import {
  exportConfigItem,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { UseMutationResult } from '@tanstack/react-query';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import { formatDateAndTime } from '../../utils/date';

interface VendorExportConfigTableProps {
  submissionContext: ISubmissionContext | ISubmissionContextJournal;
  workflowResponse: IGetSubmissionWorkflowResponse;
  submissionId: string;
  handleEditAndRetry: (item: exportConfigItem) => Promise<void>;
  handleRetry: (
    exportConfig: exportConfigItem,
    submissionId: string,
    submissionContextId: string,
    organizationId: string
  ) => Promise<void>;
  downloadVendorExport: UseMutationResult<
    ArrayBuffer,
    Error,
    {
      submissionId: string;
      exportType: string;
      submissionContextId: string;
      exportName: string;
      exportConfigId: string;
    }
  >;

  buttonsEnabled: () => boolean;
  retryLoadingMap: Record<string, boolean>;
  editRetryLoadingMap: Record<string, boolean>;
  downloadLoadingMap: Record<string, boolean>;
}
const VendorExportConfigTable = ({
  submissionContext,
  workflowResponse,
  submissionId,
  handleEditAndRetry,
  handleRetry,
  downloadVendorExport,
  buttonsEnabled,
  retryLoadingMap,
  editRetryLoadingMap,
  downloadLoadingMap
}: VendorExportConfigTableProps) => {
  const toaster = useToast();
  return (
    <>
      {submissionContext?.config.exportConfig &&
      submissionContext?.config.exportConfig.length > 0 ? (
        <Table
          variant="simple"
          bg="white"
          sx={{
            '& th, & td': {
              px: 4,
              py: 3
            }
          }}
        >
          <Thead>
            <Tr
              data-testid="exports-table-header"
              sx={{
                '& th': {
                  textTransform: 'none',
                  color: 'black',
                  background: 'white'
                }
              }}
            >
              <Th>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Export Name
                </Text>
              </Th>
              <Th>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Configure Type
                </Text>
              </Th>
              <Th>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Created
                </Text>
              </Th>
              <Th>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Updated
                </Text>
              </Th>
              <Th textAlign="left" />
            </Tr>
          </Thead>

          <Tbody>
            {submissionContext?.config.exportConfig?.map((item, index) => (
              <Tr key={index} data-testid={`exports-table-row-${index}`}>
                <Td>
                  <Flex direction="column">
                    <Text fontWeight="semibold">{item.name}</Text>
                    <Text fontSize="sm">
                      {item.exportType === 'full'
                        ? 'Package file'
                        : 'Metadata Only'}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Text>{item.exportConfigType.toUpperCase()}</Text>
                </Td>
                <Td>
                  <Text>{item.createdBy}</Text>
                  <Text fontSize="sm">
                    {item.createdAt !== '-' &&
                      formatDateAndTime(item.createdAt)}
                  </Text>
                </Td>
                <Td>
                  <Text>{item.updatedBy}</Text>
                  <Text fontSize="sm">
                    {item.updatedAt !== '-' &&
                      formatDateAndTime(item.updatedAt)}
                  </Text>
                </Td>
                <Td>
                  <Flex gap={4}>
                    <Tooltip
                      label={
                        buttonsEnabled()
                          ? 'Submission is not in formal acceptance phase'
                          : 'Download'
                      }
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                    >
                      <ProtectedAction
                        relation="task_completer"
                        entityType="journal_phase"
                        entityId={`${submissionContext?.id}_${workflowResponse?.workflowData.currentState}`}
                        tooltipLabel="You do not have permission to perform this action"
                      >
                        <ButtonIcon
                          size="small"
                          ariaLabel="Download Export"
                          icon={<FontAwesomeIcon icon={faDownload} />}
                          data-cy="contributions-info-icon"
                          onClick={() => {
                            toaster({
                              variant: 'attention',
                              title: 'Download Initiated',
                              isClosable: true
                            });
                            downloadVendorExport.mutate({
                              submissionId: submissionId,
                              exportType: item.exportType,
                              submissionContextId: submissionContext?.id,
                              exportName: item.name,
                              exportConfigId: item.id
                            });
                          }}
                          disabled={
                            downloadLoadingMap[item.id] || buttonsEnabled()
                          }
                        />
                      </ProtectedAction>
                    </Tooltip>
                    <Tooltip
                      label={
                        buttonsEnabled()
                          ? 'Submission is not in formal acceptance phase'
                          : 'Export to a new location'
                      }
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                    >
                      <ProtectedAction
                        relation="task_completer"
                        entityType="journal_phase"
                        entityId={`${submissionContext?.id}_${workflowResponse?.workflowData.currentState}`}
                        tooltipLabel="You do not have permission to perform this action"
                      >
                        <ButtonIcon
                          size="small"
                          ariaLabel="Edit Export and Retry"
                          icon={<FontAwesomeIcon icon={faFileExport} />}
                          data-cy="contributions-info-icon"
                          onClick={() => {
                            handleEditAndRetry(item);
                          }}
                          disabled={
                            retryLoadingMap[item.id] ||
                            editRetryLoadingMap[item.id] ||
                            buttonsEnabled() ||
                            item.exportConfigType === 'api' ||
                            item.exportConfigType === 'manual'
                          }
                        />
                      </ProtectedAction>
                    </Tooltip>
                    <Tooltip
                      label={
                        buttonsEnabled()
                          ? 'Submission is not in formal acceptance phase'
                          : 'Retry'
                      }
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                    >
                      <ProtectedAction
                        relation="task_completer"
                        entityType="journal_phase"
                        entityId={`${submissionContext?.id}_${workflowResponse?.workflowData.currentState}`}
                        tooltipLabel="You do not have permission to perform this action"
                      >
                        <ButtonIcon
                          size="small"
                          ariaLabel="Retry"
                          icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
                          data-cy="contributions-info-icon"
                          onClick={() => {
                            handleRetry(
                              item,
                              submissionId!,
                              submissionContext?.id,
                              submissionContext?.organizationId
                            );
                          }}
                          disabled={
                            retryLoadingMap[item.id] ||
                            editRetryLoadingMap[item.id] ||
                            buttonsEnabled() ||
                            item.exportConfigType === 'manual'
                          }
                        />
                      </ProtectedAction>
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <NoRecordFound
          title="No export configurations"
          subTitle="No export configurations found for this submission"
        />
      )}
    </>
  );
};

export default VendorExportConfigTable;
